<template>
  <div
    class="w-full fixed top-0 left-0 right-0 bottom-0 bg-[#FAFAFA] flex flex-col z-100"
  >
    <div
      class="w-full min-h-[60px] max-h-[60px] flex items-center justify-between tablet-h:justify-center bg-white p-3 tablet-h:px-[18px] tablet-h:py-3 border-b border-[#E5E5E5]"
    >
      <div class="hidden tablet-h:block w-1/3">
        <div class="flex items-center">
          <UiButton
            v-if="showDownloadButton"
            icon="Download"
            class="mr-5"
            primary
            @click="handleDownloadClick"
          >
            Download
          </UiButton>
          <UiButton icon="Share" @click="handleShareClick"> Share </UiButton>
        </div>
      </div>
      <div class="w-1/3 flex tablet-h:justify-center">
        <DacrWordLogo class="h-[30px]" />
      </div>
      <div class="w-1/3 flex items-center justify-end">
        <UiButton
          v-if="isOwner"
          class="hidden tablet-h:flex mr-5"
          icon="Settings"
          @click="handleFileSettingsClick"
        >
          Copyright Settings
        </UiButton>
        <Icon
          :size="26"
          name="Plus"
          class="stroke-gray-800 rotate-45 cursor-pointer"
          @click="handleClose"
        />
      </div>
    </div>
    <div class="h-full p-3 tablet-h:p-10 overflow-y-auto">
      <div
        v-if="isStaticPreview"
        class="w-full h-full flex items-center justify-center"
      >
        <ThumbnailIcon
          class="w-full h-full"
          :image-url="previewImageUrl"
          :image-sizes="[IMAGE_SMALL, IMAGE_LARGE, 1000]"
          :type="props.file.type"
          :icon-size="300"
          showShadow
          transition
        />
      </div>
      <div
        v-else-if="isPagesPreview"
        class="flex flex-col items-center relative"
      >
        <img
          v-for="index in totalPages"
          :key="index"
          :src="getPageUrl(index)"
          loading="lazy"
          class="max-w-[800px] w-full h-full mb-10 last:mb-0 pointer-events-none select-none"
        />
      </div>
    </div>
    <div
      class="tablet-h:hidden w-full min-h-[60px] max-h-[60px] flex items-center justify-between bg-white px-3 border-t border-[#E5E5E5]"
    >
      <div class="flex items-center">
        <UiButton
          v-if="showDownloadButton"
          primary
          icon="Download"
          class="mr-1.5"
          @click="handleDownloadClick"
        >
          Download
        </UiButton>
        <UiButton icon="Share" @click="handleShareClick"> Share </UiButton>
      </div>
      <UiButton v-if="isOwner" icon="Settings" @click="handleFileSettingsClick">
        <div v-if="isViewportWideEnough" class="font-secondary">
          Copyright settings
        </div>
      </UiButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { FilePermission, FileRole } from '@/shared/graphql/router/graphql'
import type { CopyrightFile } from '@/features/copyright/composables/useCopyrightFile'
import { useDevice } from '@/shared/composables/useDevice'
import { computed, onBeforeUnmount, onMounted } from 'vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { UiButton, Icon, ThumbnailIcon } from '@dacr/ui'
import DacrWordLogo from '@/assets/svg/other/DacrWordLogo.svg'
import { applyAuthorizationToken } from '@/shared/utils/url'
import padStart from 'lodash.padstart'
import { IMAGE_LARGE, IMAGE_SMALL } from '@/shared/utils/image'

const props = defineProps<{
  file: CopyrightFile
}>()
const emit = defineEmits<{
  closeViewer: []
}>()

const device = useDevice()
const { open } = useDialog()

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown)
})

const previewImageUrl = computed(() =>
  props.file.asset?.previewImageUrl
    ? applyAuthorizationToken(props.file.asset.previewImageUrl)
    : undefined
)

const isStaticPreview = computed(
  () => props.file.asset?.__typename !== 'DocumentAsset'
)

const isPagesPreview = computed(
  () => props.file.asset?.__typename === 'DocumentAsset'
)

const totalPages = computed(() =>
  props.file.asset?.__typename === 'DocumentAsset'
    ? props.file.asset.totalPages
    : undefined
)

const documentUrl = computed(() =>
  props.file.asset?.__typename === 'DocumentAsset'
    ? props.file.asset.documentUrl
    : undefined
)

const isOwner = computed(() => props.file.role === FileRole.Owner)

const isViewportWideEnough = computed(() => device.viewportWidth.value >= 428)

const showDownloadButton = computed(() =>
  props.file.permissions.includes(FilePermission.Download)
)

const handleClose = () => {
  emit('closeViewer')
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.code === 'Escape') {
    handleClose()
  }
}

const getPageUrl = (index: number) => {
  const url = documentUrl.value
  if (!url) return ''

  const padding = totalPages.value?.toString().length
  const pageString = padStart(index.toString(), padding, '0')

  return url.replace(/-(.*)\./, `-${pageString}.`)
}

const handleFileSettingsClick = () => {
  open(Dialogs.FileSettings, {
    props: { file: props.file },
  })
}

const handleShareClick = () => {
  open(Dialogs.Share, {
    props: { file: props.file },
  })
}

const handleDownloadClick = () => {
  open(Dialogs.Download, {
    props: { file: props.file },
  })
}
</script>
